// Error Boundary - Catches React crashes and shows friendly message
// Made by Claude and Romi

import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { ABLE_COLORS } from '../constants';

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Log error for debugging
    console.error('ErrorBoundary caught error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo?.componentStack
    });
    
    // TODO: Send to Sentry when integrated
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <AlertTriangle size={48} color={ABLE_COLORS.peach} style={styles.icon} />
            
            <h2 style={styles.title}>Something went wrong</h2>
            
            <p style={styles.message}>
              We're sorry, but something unexpected happened. 
              Your data is safe - try refreshing the page.
            </p>
            
            <div style={styles.buttonContainer}>
              <button onClick={this.handleReset} style={styles.primaryButton}>
                <RefreshCw size={18} />
                Try again
              </button>
              
              <button onClick={this.handleGoHome} style={styles.secondaryButton}>
                <Home size={18} />
                Go home
              </button>
            </div>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={styles.details}>
                <summary style={styles.summary}>Error details (dev only)</summary>
                <pre style={styles.errorText}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ABLE_COLORS.midnight,
    padding: '20px'
  },
  card: {
    backgroundColor: ABLE_COLORS.charcoal,
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '400px',
    textAlign: 'center',
    border: `1px solid ${ABLE_COLORS.slate}`
  },
  icon: {
    marginBottom: '20px'
  },
  title: {
    color: ABLE_COLORS.cloud,
    fontSize: '24px',
    fontWeight: '600',
    margin: '0 0 12px 0'
  },
  message: {
    color: ABLE_COLORS.slate,
    fontSize: '16px',
    lineHeight: '1.5',
    margin: '0 0 24px 0'
  },
  buttonContainer: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center'
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: ABLE_COLORS.peach,
    color: ABLE_COLORS.midnight,
    border: 'none',
    borderRadius: '8px',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  secondaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'transparent',
    color: ABLE_COLORS.cloud,
    border: `1px solid ${ABLE_COLORS.slate}`,
    borderRadius: '8px',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  details: {
    marginTop: '24px',
    textAlign: 'left'
  },
  summary: {
    color: ABLE_COLORS.slate,
    cursor: 'pointer',
    fontSize: '12px'
  },
  errorText: {
    backgroundColor: ABLE_COLORS.midnight,
    color: ABLE_COLORS.semantic.error,
    padding: '12px',
    borderRadius: '8px',
    fontSize: '11px',
    overflow: 'auto',
    maxHeight: '200px',
    marginTop: '8px'
  }
};

export default ErrorBoundary;
